import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import React from 'react';

import { CDNAssets } from '../../CDN';
import { ContactUsMode } from '../../components/forms/ContactForm';
import ParallaxLayoutList, { ParallaxLayoutTextImageCard } from '../../components/layout/lists/ParallaxLayoutList';
import HeaderSection, { HeaderSectionDestinationUrl } from '../../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../../components/layout/sections/PageSection';
import PageSectionTitle from '../../components/layout/sections/PageSectionTitle';
import { StrapiApiClient } from '../../lib/integrations/strapi/StrapiApiClient';
import { StrapiApiArticle, StrapiApiScrollingLogo } from '../../lib/integrations/strapi/types';
import { withGlobalDefaultStaticProps } from '../../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../../lib/seo/pageSeoConfigs';
import pageRoutes from '../../router/pageRoutes';
import { NextPageWithLayout } from '../../types/global-types';
import ScrollingLogosWidget from '../../widgets/animated/ScrollingLogosWidget';
import ContactUsWidget from '../../widgets/interactive/ContactUsWidget';
import { FeatureShowcase } from '../../widgets/showcases/FeatureShowcase/FeatureShowcase';
import HeroFeatureShowcase from '../../widgets/showcases/HeroFeatureShowcase';

// Dynamically import with SSR disabled
const LearnMoreShowcase = dynamic(() => import('../../widgets/showcases/LearnMoreShowcase'), { ssr: false });

type PlatformPageStaticProps = {
  scrollingLogos: StrapiApiScrollingLogo[];
  articles: StrapiApiArticle[];
};

export const getStaticProps: GetStaticProps<PlatformPageStaticProps> = async (context) => {
  const scrollingLogos = await StrapiApiClient.getAllScrollingLogos();
  const articles = await StrapiApiClient.getRandomMostRecentArticles();
  const props: PlatformPageStaticProps = { scrollingLogos, articles };
  return withGlobalDefaultStaticProps(context, { props });
};

const PlatformPage: NextPageWithLayout<PlatformPageStaticProps> = (props) => {
  const { scrollingLogos, articles } = props;
  const seoConfig = getPageSeoConfig(pageRoutes.Platform);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        subTitle="Gaspar AI Platform"
        title="One platform for all employee issues!"
        description="Our AI helpdesk for employee support empowers your teams to perform tasks, find answers and take informed decisions."
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
      />
      <PageSection>
        <HeroFeatureShowcase
          imageUrl={CDNAssets.getUrl('platform-page-quick-look')}
          title={'A quick look'}
          description={`Take a tour to experience our AI helpdesk software's main features for employee support`}
        />
      </PageSection>
      <PageSection type={PageSectionType.White}>
        <PageSectionTitle mode="parallax" text="The Gaspar AI helpdesk platform" />
        <ParallaxLayoutList
          renderItem={(item) => (
            <ParallaxLayoutTextImageCard imageUrl={item.imageUrl} paragraphs={item.paragraphs} description={item.description} />
          )}
          items={[
            {
              title: 'Generative AI Virtual Agent',
              data: {
                description: 'The most helpful AI assistant',
                imageUrl: CDNAssets.getUrl('platform-page-generative-ai-virtual-agent'),
                paragraphs: [
                  "Available on Slack, MS Teams and Jira SM support portal, our intuitive Virtual agent makes it smooth and easy for employees to solve their issues in seconds. They simply have a conversation with Gaspar, our always-accessible AI chatbot. It is trained on best-in-class LLMs and uses Natural Language Processing to understand employee questions' context and intent.",
                  'Our advanced dialog management capabilities ensure that employees have natural, human-like, interactive conversations. Gaspar can dynamically ask follow-up questions and combine multiple plugins until users get accurate solutions.',
                ],
              },
            },
            {
              title: 'No-code workflows',
              data: {
                description: 'Seamless workflow automation',
                imageUrl: CDNAssets.getUrl('platform-page-no-code-workflows'),
                paragraphs: [
                  "Use Gaspar AI's workflows to easily automate complex processes, and automatically fulfill repetitive tasks such as employee onboarding and offboarding.",
                  'No coding skills needed! Our easy-to-use templates allow your teams to create multiple flows, deciding which features and actions to enable. They can then easily deploy them whenever needed.',
                ],
              },
            },
            {
              title: 'Admin Hub',
              data: {
                description: 'The go-to destination for your IT managers',
                imageUrl: CDNAssets.getUrl('platform-page-admin-portal'),
                paragraphs: [
                  'Dashboard: Explore and explain the impact of your AI helpdesk by reviewing key performance metrics.',
                  'Employee insights: Gaspar AI groups employee issues and uncovers what’s blocking their productivity.',
                  'Recommendations: Identify high-impact automation, content, and process improvements at the click of a button.',
                  'Reporting: Summarize the most important charts and data for your leadership team.',
                  'Benchmarks: Compare service performance over time and take action when needed.',
                  'Workflows: Create custom workflows to automate repetitive processes.',
                ],
              },
            },
          ]}
        />
      </PageSection>
      <PageSection type={PageSectionType.BlueElectric}>
        <FeatureShowcase
          title="Unlock 100+ automations"
          description="Gaspar AI seamlessly integrates with your favorite tools to empower your teams with 100+ automations across different departments and systems."
          panels={[{ imageUrl: CDNAssets.getUrl('platform-page-automations') }]}
        />
      </PageSection>
      <PageSection isNarrow={true}>
        <ScrollingLogosWidget logos={scrollingLogos} />
      </PageSection>
      <PageSection>
        <LearnMoreShowcase articles={articles} />
      </PageSection>
      <PageSection>
        <ContactUsWidget defaultMode={ContactUsMode.FreeTrial} showAllModes={true} />
      </PageSection>
    </>
  );
};
PlatformPage.displayName = 'PlatformPage';
export default PlatformPage;
